import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/auth/AuthContext";
import { useAuth } from "@/auth/AuthContext";
import AppHeader from "@/components/app/AppHeader";
import CreateWeddingDialog from "@/components/app/CreateWeddingDialog";

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return s; }
};


function UpgradeNudge({ plan, onUpgrade, upgrading }) {
  if (plan === "premium") return null;
  return (
    <div className="rounded-[12px] px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4" style={{ border: "1px solid rgba(201,168,106,0.35)", background: "linear-gradient(to right, #fdfaf5, #fff)" }}>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] text-neutral-700 leading-relaxed">
          {plan === "free" ? (<>Sei sul piano <span className="font-medium text-neutral-900">Free</span> — limitato a 20 foto, nessun video, galleria attiva 15 giorni. <span className="text-neutral-500">Passa a Smart o Premium per sbloccare tutto.</span></>) : (<>Sei sul piano <span className="font-medium text-neutral-900">Smart</span>. Passa a <span className="font-medium text-neutral-900">Premium</span> per foto illimitate, galleria 36 mesi e il Wedding Reel AI.</>)}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {plan === "free" && (<button type="button" onClick={() => onUpgrade("smart")} disabled={!!upgrading} className="px-4 py-2 rounded-full text-[12px] font-medium border transition-all disabled:opacity-50" style={{ borderColor: "rgba(201,168,106,0.6)", color: "#9b7d40" }}>{upgrading === "smart" ? "…" : "Smart €59"}</button>)}
        <button type="button" onClick={() => onUpgrade("premium")} disabled={!!upgrading} className="px-4 py-2 rounded-full text-[12px] font-medium text-white transition-all disabled:opacity-50" style={{ background: "#c9a86a" }}>{upgrading === "premium" ? "…" : "Premium €129"}</button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [upgrading, setUpgrading] = useState(null);
  const [trash, setTrash] = useState([]);
  const [restoringId, setRestoringId] = useState(null);
  const [quota, setQuota] = useState(null); // { can_create, events_created, events_quota }
  const [confirmHardDeleteId, setConfirmHardDeleteId] = useState(null);
  const [hardDeletingId, setHardDeletingId] = useState(null);

  const plan = user?.plan || "free";
  const isVenue = user?.role === "venue";
  const canCreateMore = !loading && !!user && (quota?.can_create ?? false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: { user: authUser }, error: authErr } = await supabase.auth.getUser();
      if (authErr || !authUser) throw new Error("Non autenticato");

      const { data: quotaData } = await supabase.rpc("check_can_create_event", {
        p_user_id: authUser.id,
      });
      setQuota(quotaData ?? null);

      const { data, error } = await supabase
        .from("weddings")
        .select("id, title, slug, wedding_date, location, created_at, deleted_at")
        .eq("owner_id", authUser.id)
        .order("created_at", { ascending: false });
      if (error) throw error;

      const withCounts = await Promise.all((data || []).map(async (w) => {
        const { count } = await supabase
          .from("uploads")
          .select("id", { count: "exact", head: true })
          .eq("wedding_id", w.id);
        return { ...w, upload_count: count ?? 0 };
      }));
      setItems(withCounts.filter(w => !w.deleted_at));
      setTrash(withCounts.filter(w => !!w.deleted_at));
    } catch {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("upgrade") === "success") {
      toast.success("Pagamento completato! Il tuo piano è stato aggiornato.");
      window.history.replaceState({}, "", "/dashboard");
    }
    if (params.get("upgrade") === "cancelled") {
      toast.info("Pagamento annullato.");
      window.history.replaceState({}, "", "/dashboard");
    }
  }, []);

  const handleRestore = async (weddingId) => {
    setRestoringId(weddingId);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("weddings")
        .update({ deleted_at: null })
        .eq("id", weddingId)
        .eq("owner_id", authUser.id);
      if (error) throw error;
      toast.success("Evento ripristinato");
      await load();
    } catch {
      toast.error("Errore nel ripristino");
    } finally {
      setRestoringId(null);
    }
  };

  const handleHardDelete = async (weddingId) => {
    setHardDeletingId(weddingId);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("weddings")
        .delete()
        .eq("id", weddingId)
        .eq("owner_id", authUser.id);
      if (error) throw error;
      setConfirmHardDeleteId(null);
      toast.success("Evento eliminato definitivamente");
      await load();
    } catch {
      toast.error("Errore nell'eliminazione");
    } finally {
      setHardDeletingId(null);
    }
  };

  const handleUpgrade = async (planKey) => {
    setUpgrading(planKey);
    try {
      const { data, error } = await supabase.functions.invoke("stripe-checkout", {
        body: { plan: planKey },
      });
      if (error) throw error;
      window.location.href = data.url;
    } catch {
      toast.error("Errore nell'apertura del pagamento");
    } finally {
      setUpgrading(null);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="dashboard-root">
      <AppHeader />
      <main className="max-w-[1240px] mx-auto px-5 md:px-8 py-10 md:py-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            {isVenue ? (
              <>
                <div className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-3">Pannello location</div>
                <h1 className="font-display text-[34px] md:text-[48px] tracking-[-0.02em] font-light text-neutral-950 leading-[1.05]">
                  Bentornato{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
                </h1>
                <p className="mt-3 text-[15px] text-neutral-600 max-w-xl">
                  Crea e gestisci gli eventi per i tuoi sposi. Ogni evento include galleria, QR code e reel AI.
                </p>
              </>
            ) : (
              <>
                <div className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-3">Il tuo matrimonio</div>
                <h1 className="font-display text-[34px] md:text-[48px] tracking-[-0.02em] font-light text-neutral-950 leading-[1.05]">
                  Bentornato{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
                </h1>
                <p className="mt-3 text-[15px] text-neutral-600 max-w-xl">
                  Gestisci il tuo evento, condividi il QR code con gli ospiti e ricevi tutti i ricordi in una galleria privata.
                </p>
              </>
            )}
          </div>
          {canCreateMore ? (
            <button type="button" onClick={() => setOpen(true)} className="wf-btn-primary rounded-full px-6 py-3 text-[13px] tracking-wide self-start md:self-auto">
              {isVenue ? "+ Nuovo evento" : "+ Nuovo matrimonio"}
            </button>
          ) : plan === "free" && !isVenue ? (
            <button type="button" onClick={() => handleUpgrade("smart")} disabled={!!upgrading} className="rounded-full px-6 py-3 text-[13px] tracking-wide self-start md:self-auto font-medium transition-all disabled:opacity-50" style={{ border: "1px solid rgba(201,168,106,0.5)", color: "#9b7d40" }}>
              Upgrade per aggiungere altri
            </button>
          ) : null}
        </div>

        {isVenue && quota && (
          <div className="mt-6 flex items-center gap-3">
            <div className="text-[13px] text-neutral-600">
              <span className="font-medium text-neutral-900">{quota.events_created}</span>
              {quota.events_quota >= 999
                ? <span className="text-neutral-500"> / illimitati</span>
                : <span className="text-neutral-500"> / {quota.events_quota} eventi questo ciclo</span>
              }
            </div>
            {quota.events_quota < 999 && (
              <div className="flex-1 max-w-[200px] h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${Math.min(100, (quota.events_created / quota.events_quota) * 100)}%`,
                    background: quota.events_created >= quota.events_quota ? "#dc2626" : "#c9a86a",
                  }}
                />
              </div>
            )}
          </div>
        )}

        {plan !== "premium" && !isVenue && <div className="mt-8"><UpgradeNudge plan={plan} onUpgrade={handleUpgrade} upgrading={upgrading} /></div>}

        <section className="mt-10">
          {loading && <div className="text-[13px] tracking-[0.18em] uppercase text-neutral-500">Caricamento…</div>}
          {!loading && items.length === 0 && (
            <div className="border border-dashed border-neutral-300 rounded-[14px] p-10 md:p-16 text-center bg-[color:var(--wf-surface)]">
              <div className="font-display text-[26px] md:text-[32px] tracking-[-0.01em] font-light text-neutral-950">
                {isVenue ? "Nessun evento ancora." : "Nessun matrimonio ancora."}
              </div>
              <p className="mt-3 text-[14px] text-neutral-600 max-w-md mx-auto">
                {isVenue
                  ? "Crea il primo evento per i tuoi sposi e condividi il QR code."
                  : "Crea il tuo evento e ricevi il QR code da condividere con gli ospiti."}
              </p>
              <button type="button" onClick={() => setOpen(true)} className="mt-8 wf-btn-primary rounded-full px-6 py-3 text-[13px] tracking-wide">
                {isVenue ? "Crea evento" : "Crea il tuo matrimonio"}
              </button>
            </div>
          )}
          {!loading && items.length > 0 && !isVenue && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((w) => (
                <Link key={w.id} to={`/dashboard/${w.id}`} className="group bg-white border border-neutral-200 rounded-[14px] p-7 flex flex-col gap-5 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_80px_-40px_rgba(10,10,10,0.2)]">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[11px] tracking-[0.22em] uppercase text-neutral-500">{fmtDate(w.wedding_date)}</div>
                      <h3 className="mt-2 font-display text-[24px] tracking-[-0.01em] font-medium text-neutral-950 leading-tight">{w.title}</h3>
                      {w.location && <div className="mt-1 text-[13px] text-neutral-600">{w.location}</div>}
                    </div>
                    <span aria-hidden className="w-2 h-2 rounded-full mt-3 flex-none" style={{ background: "#c9a86a" }} />
                  </div>
                  <div className="mt-auto pt-5 border-t border-neutral-200 flex items-center justify-between">
                    <div className="flex items-baseline gap-2">
                      <span className="font-display text-[22px] font-light tracking-tight text-neutral-950">{w.upload_count ?? 0}</span>
                      <span className="text-[11px] tracking-[0.18em] uppercase text-neutral-500">Ricordi</span>
                    </div>
                    <span className="text-[12px] text-neutral-500 group-hover:text-[color:var(--wf-gold)] transition-colors">Apri →</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
          {!loading && items.length > 0 && isVenue && (() => {
            const today = new Date().toISOString().slice(0, 10);
            const upcomingEvents = items.filter(w => w.wedding_date && w.wedding_date >= today);
            const pastEvents = items.filter(w => w.wedding_date && w.wedding_date < today);
            const undatedEvents = items.filter(w => !w.wedding_date);
            const EventCard = ({ w }) => (
              <Link key={w.id} to={`/dashboard/${w.id}`} className="group bg-white border border-neutral-200 rounded-[14px] p-6 flex flex-col gap-4 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_80px_-40px_rgba(10,10,10,0.2)]">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[11px] tracking-[0.22em] uppercase text-neutral-500">{fmtDate(w.wedding_date)}</div>
                    <h3 className="mt-1 font-display text-[22px] tracking-[-0.01em] font-medium text-neutral-950 leading-tight">{w.title}</h3>
                    {w.location && <div className="mt-0.5 text-[12px] text-neutral-500">{w.location}</div>}
                  </div>
                  <span className="text-[12px] text-neutral-400 group-hover:text-[color:var(--wf-gold)] transition-colors mt-1 flex-none">Apri →</span>
                </div>
                <div className="pt-4 border-t border-neutral-100 flex items-baseline gap-2">
                  <span className="font-display text-[20px] font-light tracking-tight text-neutral-950">{w.upload_count ?? 0}</span>
                  <span className="text-[10px] tracking-[0.18em] uppercase text-neutral-400">Ricordi</span>
                </div>
              </Link>
            );
            return (
              <div className="flex flex-col gap-10">
                {upcomingEvents.length > 0 && (
                  <div>
                    <div className="text-[11px] tracking-[0.18em] uppercase text-neutral-400 mb-4">Prossimi ({upcomingEvents.length})</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      {upcomingEvents.map(w => <EventCard key={w.id} w={w} />)}
                    </div>
                  </div>
                )}
                {undatedEvents.length > 0 && (
                  <div>
                    <div className="text-[11px] tracking-[0.18em] uppercase text-neutral-400 mb-4">Data da definire ({undatedEvents.length})</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      {undatedEvents.map(w => <EventCard key={w.id} w={w} />)}
                    </div>
                  </div>
                )}
                {pastEvents.length > 0 && (
                  <div>
                    <div className="text-[11px] tracking-[0.18em] uppercase text-neutral-400 mb-4">Passati ({pastEvents.length})</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      {pastEvents.map(w => <EventCard key={w.id} w={w} />)}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </section>

        {/* Cestino */}
        {trash.length > 0 && (
          <section className="mt-12">
            <details className="group">
              <summary className="cursor-pointer list-none flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-neutral-400 hover:text-neutral-600 transition select-none">
                <span className="inline-block w-3 h-3 border border-current rounded-sm flex items-center justify-center text-[8px]">
                  <span className="group-open:rotate-180 transition-transform inline-block">▼</span>
                </span>
                Cestino ({trash.length})
                <span className="text-neutral-400 normal-case tracking-normal text-[11px] font-normal ml-1">— eliminazione definitiva dopo 30 giorni</span>
              </summary>
              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {trash.map((w) => (
                  <div key={w.id} className="bg-neutral-50 border border-neutral-200 rounded-[14px] p-6 flex flex-col gap-3 opacity-70">
                    <div>
                      <div className="text-[10px] tracking-[0.22em] uppercase text-neutral-400">{fmtDate(w.wedding_date) || "—"}</div>
                      <div className="mt-1 font-display text-[20px] tracking-[-0.01em] font-light text-neutral-700 leading-tight">{w.title}</div>
                      {w.location && <div className="text-[12px] text-neutral-500 mt-0.5">{w.location}</div>}
                    </div>
                    <div className="text-[11px] text-neutral-500">
                      Eliminato il {fmtDate(w.deleted_at)}
                    </div>
                    {confirmHardDeleteId === w.id ? (
                      <div className="flex flex-col gap-2">
                        <span className="text-[11px] text-red-600">Eliminazione definitiva: impossibile annullare.</span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setConfirmHardDeleteId(null)}
                            className="border border-neutral-300 rounded-full px-3 py-1.5 text-[11px] text-neutral-600 hover:bg-white transition"
                          >
                            Annulla
                          </button>
                          <button
                            type="button"
                            onClick={() => handleHardDelete(w.id)}
                            disabled={hardDeletingId === w.id}
                            className="rounded-full px-3 py-1.5 text-[11px] bg-red-600 text-white disabled:opacity-50 transition"
                          >
                            {hardDeletingId === w.id ? "Eliminazione…" : "Conferma eliminazione"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleRestore(w.id)}
                          disabled={restoringId === w.id}
                          className="border border-neutral-300 rounded-full px-4 py-2 text-[12px] tracking-wide text-neutral-600 hover:bg-white hover:border-neutral-400 transition disabled:opacity-50"
                        >
                          {restoringId === w.id ? "Ripristino…" : "Ripristina"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmHardDeleteId(w.id)}
                          className="border border-red-200 rounded-full px-4 py-2 text-[12px] tracking-wide text-red-500 hover:bg-red-50 hover:border-red-300 transition"
                        >
                          Elimina definitivamente
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </details>
          </section>
        )}
      </main>
      <CreateWeddingDialog open={open} onClose={() => setOpen(false)} onCreated={() => load()} />
    </div>
  );
}
